import { useEffect, useState } from 'react'

/**
 * Fetches a Downloads-tab preview endpoint whenever period/to changes, while the card is
 * open. Every preview endpoint reuses the exact data function its xlsx/pdf builds from
 * (see serving/app.py), so this hook is the only fetch logic the four preview panels need.
 */
export function useReportPreview<T>(
  endpoint: string, period: 'daily' | 'weekly' | 'monthly', to: string, active: boolean,
): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!active) return
    let alive = true
    setLoading(true)
    setError(null)
    const q = new URLSearchParams({ period })
    if (to) q.set('to', to)
    fetch(`${endpoint}?${q}`)
      .then(async (r) => {
        const body = await r.json() as T & { error?: string }
        if (!r.ok || (body as { error?: string }).error) {
          throw new Error((body as { error?: string }).error ?? String(r.status))
        }
        if (alive) setData(body)
      })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [endpoint, period, to, active])

  return { data, loading, error }
}
